import { LitElement, html, nothing } from 'lit';
import { unsafeHTML } from 'lit/directives/unsafe-html.js';
import { customElement, property, state } from 'lit/decorators.js';
import { marked } from 'marked';
import DOMPurify from 'dompurify';
import type { FounderTicket, FounderTicketStatus, BrowseResult, MentionData } from '../../services/founder-board-api.js';
import { ticketTypeLabel, respondToTicket, approveToolRequest, parseToolRequestMeta, parseProjectCreationMeta, parseContentPreviewMeta, approveProjectCreation, approveContentPreview, browseFilesystem, driveUrlToThumbnail } from '../../services/founder-board-api.js';
import type { CpAgentMentionDropdown } from '../shared/cp-agent-mention-dropdown.js';

@customElement('cp-founder-ticket-card')
export class CpFounderTicketCard extends LitElement {
  createRenderRoot() { return this; }

  @property({ type: Object }) ticket!: FounderTicket;

  @state() private _panelOpen = false;
  @state() private _showReply = false;
  @state() private _replyComment = '';
  @state() private _submitting = false;
  @state() private _error = '';
  // Content preview state
  @state() private _contentCaptionEdit = '';
  @state() private _showContentCaptionEdit = false;
  // Project creation form state
  @state() private _showProjectForm = false;
  @state() private _projectPath = '';
  @state() private _gitInit = false;
  @state() private _gitRemoteUrl = '';
  @state() private _browseResult: BrowseResult | null = null;
  @state() private _showBrowser = false;
  // @-mention state
  @state() private _mentions: MentionData[] = [];
  @state() private _mentionOpen = false;
  @state() private _mentionQuery = '';
  private _mentionStartIndex = -1;

  private _onTextareaInput(e: Event): void {
    const textarea = e.target as HTMLTextAreaElement;
    this._replyComment = textarea.value;
    const cursorPos = textarea.selectionStart;
    const textBefore = textarea.value.substring(0, cursorPos);

    // Check if we're in an @-mention context
    const atMatch = textBefore.match(/@([^\s@]*)$/);
    if (atMatch) {
      this._mentionStartIndex = cursorPos - atMatch[1].length - 1; // include the @
      this._mentionQuery = atMatch[1];
      this._mentionOpen = true;
    } else {
      this._mentionOpen = false;
    }
  }

  private _onTextareaKeydown(e: KeyboardEvent): void {
    if (this._mentionOpen) {
      const dropdown = this.querySelector('cp-agent-mention-dropdown') as CpAgentMentionDropdown | null;
      if (dropdown?.handleKeydown(e)) {
        e.stopPropagation();
        return;
      }
    }
  }

  private _onAgentSelected(e: CustomEvent<{ agentId: number; agentName: string }>): void {
    const { agentId, agentName } = e.detail;
    // Insert @AgentName into textarea, replacing the @query
    const before = this._replyComment.substring(0, this._mentionStartIndex);
    const after = this._replyComment.substring(this._mentionStartIndex + 1 + this._mentionQuery.length);
    this._replyComment = `${before}@${agentName}${after ? '' : ' '}${after}`;
    // Track mention
    if (!this._mentions.some(m => m.agent_id === agentId)) {
      this._mentions = [...this._mentions, { agent_id: agentId, agent_name: agentName }];
    }
    this._mentionOpen = false;
    this._mentionQuery = '';
    // Re-focus textarea
    this.updateComplete.then(() => {
      const textarea = this.querySelector('.cp-ticket-card__textarea') as HTMLTextAreaElement | null;
      if (textarea) {
        const cursorPos = before.length + 1 + agentName.length + (after ? 0 : 1);
        textarea.focus();
        textarea.setSelectionRange(cursorPos, cursorPos);
      }
    });
  }

  private _onMentionDismissed(): void {
    this._mentionOpen = false;
    this._mentionQuery = '';
  }

  private _onTextareaBlur(): void {
    // Small delay so mousedown/touchend on dropdown items fires first
    setTimeout(() => {
      this._mentionOpen = false;
    }, 200);
  }

  private async _handleRespond(newStatus: FounderTicketStatus): Promise<void> {
    if (!this._replyComment.trim()) return;
    this._submitting = true;
    this._error = '';
    try {
      const mentions = this._mentions.length > 0 ? this._mentions : undefined;
      const updated = await respondToTicket(this.ticket.id, this._replyComment.trim(), newStatus, mentions);
      this._panelOpen = false;
      this.dispatchEvent(new CustomEvent('ticket-updated', {
        detail: { ticket: updated },
        bubbles: true,
        composed: true,
      }));
      this._showReply = false;
      this._replyComment = '';
      this._mentions = [];
    } catch (e) {
      this._error = e instanceof Error ? e.message : 'Fehler beim Antworten';
    } finally {
      this._submitting = false;
    }
  }

  private async _handleDismiss(): Promise<void> {
    this._submitting = true;
    this._error = '';
    try {
      const updated = await respondToTicket(this.ticket.id, 'Thema verworfen – Gruender hat das Ticket geschlossen.', 'done');
      this._panelOpen = false;
      this.dispatchEvent(new CustomEvent('ticket-updated', {
        detail: { ticket: updated },
        bubbles: true,
        composed: true,
      }));
      this._showReply = false;
      this._replyComment = '';
    } catch (e) {
      this._error = e instanceof Error ? e.message : 'Fehler beim Schliessen';
    } finally {
      this._submitting = false;
    }
  }

  private async _handleApproveTool(): Promise<void> {
    this._submitting = true;
    this._error = '';
    try {
      const updated = await approveToolRequest(this.ticket.id);
      this._panelOpen = false;
      this.dispatchEvent(new CustomEvent('ticket-updated', {
        detail: { ticket: updated },
        bubbles: true,
        composed: true,
      }));
      this._showReply = false;
      this._replyComment = '';
    } catch (e) {
      this._error = e instanceof Error ? e.message : 'Fehler beim Genehmigen';
    } finally {
      this._submitting = false;
    }
  }

  private async _handleApproveContent(): Promise<void> {
    this._submitting = true;
    this._error = '';
    try {
      const meta = parseContentPreviewMeta(this.ticket.description);
      const editedCaption = this._showContentCaptionEdit && this._contentCaptionEdit !== meta?.caption
        ? this._contentCaptionEdit
        : undefined;
      const updated = await approveContentPreview(this.ticket.id, editedCaption);
      this._panelOpen = false;
      this.dispatchEvent(new CustomEvent('ticket-updated', {
        detail: { ticket: updated },
        bubbles: true,
        composed: true,
      }));
      this._showContentCaptionEdit = false;
    } catch (e) {
      this._error = e instanceof Error ? e.message : 'Fehler beim Genehmigen';
    } finally {
      this._submitting = false;
    }
  }

  private async _handleApproveProject(): Promise<void> {
    if (!this._projectPath.trim()) return;
    this._submitting = true;
    this._error = '';
    try {
      const updated = await approveProjectCreation(
        this.ticket.id,
        this._projectPath.trim(),
        this._gitInit,
        this._gitRemoteUrl.trim() || undefined,
      );
      this._panelOpen = false;
      this._showProjectForm = false;
      this.dispatchEvent(new CustomEvent('ticket-updated', {
        detail: { ticket: updated },
        bubbles: true,
        composed: true,
      }));
    } catch (e) {
      this._error = e instanceof Error ? e.message : 'Fehler beim Anlegen';
    } finally {
      this._submitting = false;
    }
  }

  private async _browseTo(targetPath?: string): Promise<void> {
    try {
      this._browseResult = await browseFilesystem(targetPath);
      this._showBrowser = true;
    } catch (e) {
      this._error = e instanceof Error ? e.message : 'Fehler beim Laden';
    }
  }

  private _selectBrowsePath(dirPath: string): void {
    this._projectPath = dirPath;
    this._showBrowser = false;
  }

  private _renderCommentWithMentions(text: string): ReturnType<typeof html> {
    // Split on @AgentName patterns and render as badges
    const parts: Array<ReturnType<typeof html>> = [];
    let lastIndex = 0;
    const mentionRegex = /@(\w[\w\s]*?\w|\w)/g;
    let match: RegExpExecArray | null;
    while ((match = mentionRegex.exec(text)) !== null) {
      if (match.index > lastIndex) {
        parts.push(html`${text.substring(lastIndex, match.index)}`);
      }
      parts.push(html`<span class="cp-mention-badge">@${match[1]}</span>`);
      lastIndex = match.index + match[0].length;
    }
    if (lastIndex < text.length) {
      parts.push(html`${text.substring(lastIndex)}`);
    }
    return html`${parts}`;
  }

  private _renderMarkdown(content: string): ReturnType<typeof html> {
    const rawHtml = marked.parse(content, { async: false }) as string;
    const sanitized = DOMPurify.sanitize(rawHtml);
    return html`${unsafeHTML(sanitized)}`;
  }

  private _openPanel(): void {
    this._panelOpen = true;
  }

  private _closePanel(): void {
    this._panelOpen = false;
    this._showReply = false;
    this._replyComment = '';
    this._error = '';
    this._mentions = [];
    this._mentionOpen = false;
  }

  private _onOverlayClick(e: MouseEvent): void {
    if ((e.target as HTMLElement).classList.contains('cp-ft-panel__overlay')) {
      this._closePanel();
    }
  }

  private _onKeyDown = (e: KeyboardEvent): void => {
    if (e.key === 'Escape' && this._panelOpen) this._closePanel();
  };

  override connectedCallback(): void {
    super.connectedCallback();
    document.addEventListener('keydown', this._onKeyDown);
  }

  override disconnectedCallback(): void {
    super.disconnectedCallback();
    document.removeEventListener('keydown', this._onKeyDown);
  }

  // --- Card (compact inline view) ---

  private _renderCard() {
    const { ticket } = this;
    const isToolRequest = ticket.ticket_type === 'tool_request';
    const isProjectCreation = ticket.ticket_type === 'project_creation';
    const isContentPreview = ticket.ticket_type === 'content_preview';
    const toolMeta = isToolRequest ? parseToolRequestMeta(ticket.description) : null;
    const projectMeta = isProjectCreation ? parseProjectCreationMeta(ticket.description) : null;
    const contentMeta = isContentPreview ? parseContentPreviewMeta(ticket.description) : null;

    return html`
      <div class="cp-ticket-card" @click=${() => this._openPanel()}>
        <div class="cp-ticket-card__header">
          <span class="cp-ticket-card__priority-dot cp-ticket-card__priority-dot--${ticket.priority}"></span>
          <div class="cp-ticket-card__title-area">
            <p class="cp-ticket-card__title">${ticket.title}</p>
            <p class="cp-ticket-card__date">
              ${new Date(ticket.created_at).toLocaleDateString('de-DE', { day: '2-digit', month: '2-digit' })}
            </p>
          </div>
          <span class="cp-ticket-card__type-badge cp-ticket-card__type-badge--${ticket.ticket_type}">
            ${isToolRequest ? html`&#x1F527; Tool-Anfrage` : isProjectCreation ? html`&#x1F4C1; Projekt-Anfrage` : isContentPreview ? html`&#x1F4F7; Instagram` : ticketTypeLabel(ticket.ticket_type)}
          </span>
        </div>

        ${toolMeta ? html`
          <div class="cp-ticket-card__tool-meta">
            <span class="cp-ticket-card__tool-name">&#x1F527; ${toolMeta.tool_name}</span>
            <p class="cp-ticket-card__tool-reason">${toolMeta.reason}</p>
          </div>
        ` : projectMeta ? html`
          <div class="cp-ticket-card__project-meta">
            <span class="cp-ticket-card__project-name">&#x1F4C1; ${projectMeta.project_name}</span>
            <p class="cp-ticket-card__project-reason">${projectMeta.reason}</p>
          </div>
        ` : contentMeta ? html`
          <div class="cp-ticket-card__content-preview-meta">
            ${contentMeta.image_urls[0] ? html`
              <img class="cp-ticket-card__content-thumb" src="${driveUrlToThumbnail(contentMeta.image_urls[0])}" alt="Preview" />
            ` : nothing}
            <p class="cp-ticket-card__tool-reason">${contentMeta.caption.slice(0, 100)}${contentMeta.caption.length > 100 ? '...' : ''}</p>
          </div>
        ` : html`
          <div class="cp-ticket-card__description cp-ticket-card__description--clamp">${this._renderMarkdown(ticket.description)}</div>
        `}

        ${ticket.founder_comment ? html`
          <div class="cp-ticket-card__reply">
            <p class="cp-ticket-card__reply-label">Meine Antwort:</p>
            <p class="cp-ticket-card__reply-text">${this._renderCommentWithMentions(ticket.founder_comment)}</p>
          </div>
        ` : nothing}
      </div>
    `;
  }

  // --- Panel (fullscreen detail view) ---

  private _renderBrowser() {
    if (!this._browseResult) return nothing;
    return html`
      <div class="cp-project-browser">
        <div class="cp-project-browser__header">
          <span class="cp-project-browser__path">${this._browseResult.current}</span>
          ${this._browseResult.parent ? html`
            <button class="cp-ticket-card__btn cp-ticket-card__btn--ghost"
              @click=${() => this._browseTo(this._browseResult!.parent!)}>&#x2191; Hoch</button>
          ` : nothing}
        </div>
        <div class="cp-project-browser__list">
          ${this._browseResult.entries.length === 0 ? html`
            <p class="cp-project-browser__empty">Keine Unterverzeichnisse</p>
          ` : this._browseResult.entries.map(entry => html`
            <div class="cp-project-browser__entry"
              @click=${() => this._browseTo(`${this._browseResult!.current}/${entry.name}`)}>
              <span class="cp-project-browser__icon">&#x1F4C1;</span>
              <span class="cp-project-browser__name">${entry.name}</span>
            </div>
          `)}
        </div>
        <div class="cp-ticket-card__actions">
          <button class="cp-ticket-card__btn cp-ticket-card__btn--primary"
            @click=${() => this._selectBrowsePath(this._browseResult!.current)}>
            Diesen Ordner waehlen
          </button>
          <button class="cp-ticket-card__btn cp-ticket-card__btn--ghost"
            @click=${() => { this._showBrowser = false; }}>Abbrechen</button>
        </div>
      </div>
    `;
  }

  private _renderProjectForm() {
    const meta = parseProjectCreationMeta(this.ticket.description);
    return html`
      <div class="cp-project-form">
        <div class="cp-project-form__field">
          <label class="cp-project-form__label">Projektname</label>
          <input type="text" class="cp-project-form__input" .value=${meta?.project_name ?? ''} disabled />
        </div>
        <div class="cp-project-form__field">
          <label class="cp-project-form__label">Pfad</label>
          <div class="cp-project-form__path-row">
            <input type="text" class="cp-project-form__input cp-project-form__input--path"
              placeholder="/Users/.../Entwicklung/projekte/neues-projekt"
              .value=${this._projectPath}
              @input=${(e: Event) => { this._projectPath = (e.target as HTMLInputElement).value; }} />
            <button class="cp-ticket-card__btn cp-ticket-card__btn--ghost"
              @click=${() => this._browseTo(this._projectPath || '/Users')}>Durchsuchen</button>
          </div>
        </div>
        ${this._showBrowser ? this._renderBrowser() : nothing}
        <div class="cp-project-form__field">
          <label class="cp-project-form__checkbox-label">
            <input type="checkbox" .checked=${this._gitInit}
              @change=${(e: Event) => { this._gitInit = (e.target as HTMLInputElement).checked; }} />
            Git initialisieren
          </label>
        </div>
        ${this._gitInit ? html`
          <div class="cp-project-form__field">
            <label class="cp-project-form__label">Git Remote URL (optional)</label>
            <input type="text" class="cp-project-form__input"
              placeholder="https://github.com/..."
              .value=${this._gitRemoteUrl}
              @input=${(e: Event) => { this._gitRemoteUrl = (e.target as HTMLInputElement).value; }} />
          </div>
        ` : nothing}
        <div class="cp-ticket-card__actions">
          <button class="cp-ticket-card__btn cp-ticket-card__btn--approve"
            ?disabled=${this._submitting || !this._projectPath.trim()}
            @click=${() => this._handleApproveProject()}>
            Anlegen & Genehmigen
          </button>
          <button class="cp-ticket-card__btn cp-ticket-card__btn--ghost"
            @click=${() => { this._showProjectForm = false; }}>Abbrechen</button>
        </div>
      </div>
    `;
  }

  private _renderPanel() {
    const { ticket } = this;
    const isToolRequest = ticket.ticket_type === 'tool_request';
    const isProjectCreation = ticket.ticket_type === 'project_creation';
    const isContentPreview = ticket.ticket_type === 'content_preview';
    const toolMeta = isToolRequest ? parseToolRequestMeta(ticket.description) : null;
    const projectMeta = isProjectCreation ? parseProjectCreationMeta(ticket.description) : null;
    const contentMeta = isContentPreview ? parseContentPreviewMeta(ticket.description) : null;

    return html`
      <div class="cp-ft-panel__overlay" @click=${this._onOverlayClick}>
        <div class="cp-ft-panel">
          <div class="cp-ft-panel__header">
            <div>
              <h2 class="cp-ft-panel__title">${isContentPreview ? 'Instagram Post Vorschau' : ticket.title}</h2>
              <p class="cp-ft-panel__meta">
                ${new Date(ticket.created_at).toLocaleDateString('de-DE', { day: '2-digit', month: '2-digit', year: 'numeric' })}
              </p>
            </div>
            <div class="cp-ft-panel__header-right">
              <span class="cp-ticket-card__type-badge cp-ticket-card__type-badge--${ticket.ticket_type}">
                ${isToolRequest ? html`&#x1F527; Tool-Anfrage` : isProjectCreation ? html`&#x1F4C1; Projekt-Anfrage` : isContentPreview ? html`&#x1F4F7; Instagram` : ticketTypeLabel(ticket.ticket_type)}
              </span>
              <button class="cp-ft-panel__close" @click=${() => this._closePanel()}>&times;</button>
            </div>
          </div>

          <div class="cp-ft-panel__body">
            ${toolMeta ? html`
              <div class="cp-ft-panel__section">
                <div class="cp-ticket-card__tool-meta">
                  <span class="cp-ticket-card__tool-name">&#x1F527; ${toolMeta.tool_name}</span>
                  <p class="cp-ticket-card__tool-reason">${toolMeta.reason}</p>
                </div>
              </div>
            ` : projectMeta ? html`
              <div class="cp-ft-panel__section">
                <div class="cp-ticket-card__project-meta">
                  <span class="cp-ticket-card__project-name">&#x1F4C1; ${projectMeta.project_name}</span>
                  <p class="cp-ticket-card__project-reason">${projectMeta.reason}</p>
                </div>
              </div>
            ` : contentMeta ? html`
              <div class="cp-ft-panel__section">
                ${contentMeta.image_urls.map(url => html`
                  <img src="${driveUrlToThumbnail(url)}" alt="Post-Bild" style="width:100%;border-radius:var(--radius-lg);margin-bottom:var(--spacing-sm);" loading="lazy" />
                `)}
              </div>
              <div class="cp-ft-panel__section">
                <h3 class="cp-ft-panel__section-label">Caption</h3>
                <p style="font-size:var(--font-size-sm);color:var(--color-text-secondary);line-height:var(--line-height-relaxed);margin:0;white-space:pre-wrap;">${contentMeta.caption}</p>
              </div>
              ${contentMeta.hashtags.length > 0 ? html`
                <div class="cp-ft-panel__section">
                  <h3 class="cp-ft-panel__section-label">Hashtags</h3>
                  <div style="display:flex;flex-wrap:wrap;gap:var(--spacing-xs);">
                    ${contentMeta.hashtags.map(tag => html`
                      <span style="padding:2px var(--spacing-sm);border-radius:var(--radius-full);font-size:var(--font-size-xs);background:rgba(0,212,255,0.1);color:var(--color-accent-primary);font-weight:var(--font-weight-medium);">${tag.startsWith('#') ? tag : `#${tag}`}</span>
                    `)}
                  </div>
                </div>
              ` : nothing}
              <div class="cp-ft-panel__section">
                <p style="font-size:var(--font-size-sm);color:var(--color-text-tertiary);">
                  ${contentMeta.posting_time ? `Posting: ${contentMeta.posting_time} · ` : ''}Typ: ${contentMeta.post_type === 'carousel' ? 'Carousel' : contentMeta.post_type === 'reel' ? 'Reel' : 'Post'}
                </p>
              </div>
            ` : html`
              <div class="cp-ft-panel__section">
                <div class="cp-ft-panel__content-md">${this._renderMarkdown(ticket.description)}</div>
              </div>
            `}

            ${ticket.founder_comment ? html`
              <div class="cp-ft-panel__section">
                <h3 class="cp-ft-panel__section-label">Meine Antwort</h3>
                <p class="cp-ft-panel__comment">${this._renderCommentWithMentions(ticket.founder_comment)}</p>
              </div>
            ` : nothing}
          </div>

          <div class="cp-ft-panel__footer">
            ${this._error ? html`<p class="cp-ticket-card__error">${this._error}</p>` : nothing}

            ${isProjectCreation && ticket.status === 'open' && !this._showProjectForm && !this._showReply ? html`
              <div class="cp-ticket-card__actions">
                <button class="cp-ticket-card__btn cp-ticket-card__btn--approve"
                  @click=${() => { this._showProjectForm = true; }}>
                  &#x1F4C1; Projekt anlegen
                </button>
                <button class="cp-ticket-card__btn cp-ticket-card__btn--ghost"
                  @click=${() => { this._showReply = true; }}>
                  Ablehnen / Alternative
                </button>
              </div>
            ` : nothing}

            ${this._showProjectForm ? this._renderProjectForm() : nothing}

            ${isContentPreview && ticket.status === 'open' && !this._showReply && !this._showContentCaptionEdit ? html`
              <div class="cp-ticket-card__actions">
                <button class="cp-ticket-card__btn cp-ticket-card__btn--success"
                  ?disabled=${this._submitting}
                  @click=${() => this._handleApproveContent()}>
                  Genehmigen & Posten
                </button>
                <button class="cp-ticket-card__btn cp-ticket-card__btn--ghost"
                  @click=${() => { this._showContentCaptionEdit = true; this._contentCaptionEdit = contentMeta?.caption ?? ''; }}>
                  Caption anpassen
                </button>
                <button class="cp-ticket-card__btn cp-ticket-card__btn--ghost"
                  @click=${() => { this._showReply = true; }}>
                  Ablehnen
                </button>
              </div>
            ` : nothing}

            ${this._showContentCaptionEdit ? html`
              <div class="cp-ticket-card__reply-form">
                <textarea
                  rows="4"
                  .value=${this._contentCaptionEdit}
                  @input=${(e: Event) => { this._contentCaptionEdit = (e.target as HTMLTextAreaElement).value; }}
                  class="cp-ticket-card__textarea"
                  style="resize:vertical;min-height:80px;"
                ></textarea>
                <div class="cp-ticket-card__actions">
                  <button class="cp-ticket-card__btn cp-ticket-card__btn--success"
                    ?disabled=${this._submitting || !this._contentCaptionEdit.trim()}
                    @click=${() => this._handleApproveContent()}>
                    Mit neuer Caption genehmigen
                  </button>
                  <button class="cp-ticket-card__btn cp-ticket-card__btn--ghost"
                    @click=${() => { this._showContentCaptionEdit = false; }}>Abbrechen</button>
                </div>
              </div>
            ` : nothing}

            ${isToolRequest && ticket.status === 'open' && !this._showReply ? html`
              <div class="cp-ticket-card__actions">
                <button class="cp-ticket-card__btn cp-ticket-card__btn--approve"
                  ?disabled=${this._submitting} @click=${() => this._handleApproveTool()}>
                  &#x2713; Tool genehmigen
                </button>
                <button class="cp-ticket-card__btn cp-ticket-card__btn--ghost"
                  @click=${() => { this._showReply = true; }}>
                  Ablehnen / Alternative
                </button>
              </div>
            ` : nothing}

            ${!isToolRequest && !isProjectCreation && !isContentPreview && !this._showReply && ticket.status !== 'done' ? html`
              <div class="cp-ticket-card__actions">
                <button class="cp-ticket-card__reply-link" @click=${() => { this._showReply = true; }}>
                  Antworten
                </button>
                <button class="cp-ticket-card__btn cp-ticket-card__btn--muted"
                  ?disabled=${this._submitting} @click=${() => this._handleDismiss()}>
                  Schliessen
                </button>
                <button class="cp-ticket-card__close-btn" title="Ticket schliessen (ohne Antwort)"
                  @click=${() => this._handleDismiss()}>
                  &#10006;
                </button>
              </div>
            ` : nothing}

            ${this._showReply ? html`
              <div class="cp-ticket-card__reply-form">
                <div style="position: relative;">
                  <cp-agent-mention-dropdown
                    .open=${this._mentionOpen}
                    .query=${this._mentionQuery}
                    @agent-selected=${(e: CustomEvent) => this._onAgentSelected(e)}
                    @mention-dismissed=${() => this._onMentionDismissed()}
                  ></cp-agent-mention-dropdown>
                  <textarea
                    rows="3"
                    placeholder=${isToolRequest || isProjectCreation ? 'Ablehnungsgrund oder Alternative...' : 'Kommentar... (@ fuer Mention)'}
                    .value=${this._replyComment}
                    @input=${(e: Event) => this._onTextareaInput(e)}
                    @keydown=${(e: KeyboardEvent) => this._onTextareaKeydown(e)}
                    @blur=${() => this._onTextareaBlur()}
                    class="cp-ticket-card__textarea"
                  ></textarea>
                </div>
                <div class="cp-ticket-card__actions">
                  ${isToolRequest ? html`
                    <button class="cp-ticket-card__btn cp-ticket-card__btn--warning"
                      ?disabled=${this._submitting || !this._replyComment.trim()}
                      @click=${() => this._handleRespond('waiting')}>Alternative vorschlagen</button>
                    <button class="cp-ticket-card__btn cp-ticket-card__btn--danger"
                      ?disabled=${this._submitting || !this._replyComment.trim()}
                      @click=${() => this._handleRespond('done')}>Ablehnen</button>
                  ` : isProjectCreation ? html`
                    <button class="cp-ticket-card__btn cp-ticket-card__btn--warning"
                      ?disabled=${this._submitting || !this._replyComment.trim()}
                      @click=${() => this._handleRespond('waiting')}>Alternative vorschlagen</button>
                    <button class="cp-ticket-card__btn cp-ticket-card__btn--danger"
                      ?disabled=${this._submitting || !this._replyComment.trim()}
                      @click=${() => this._handleRespond('done')}>Ablehnen</button>
                  ` : html`
                    <button class="cp-ticket-card__btn cp-ticket-card__btn--success"
                      ?disabled=${this._submitting || !this._replyComment.trim()}
                      @click=${() => this._handleRespond('approved')}>Genehmigen</button>
                    <button class="cp-ticket-card__btn cp-ticket-card__btn--primary"
                      ?disabled=${this._submitting || !this._replyComment.trim()}
                      @click=${() => this._handleRespond('done')}>Erledigt</button>
                    <button class="cp-ticket-card__btn cp-ticket-card__btn--muted"
                      ?disabled=${this._submitting || !this._replyComment.trim()}
                      @click=${() => this._handleRespond('waiting')}>Warte auf Agent</button>
                  `}
                  <button class="cp-ticket-card__btn cp-ticket-card__btn--ghost"
                    @click=${() => { this._showReply = false; this._replyComment = ''; }}>Abbrechen</button>
                </div>
              </div>
            ` : nothing}
          </div>
        </div>
      </div>
    `;
  }

  override render() {
    return html`
      ${this._renderCard()}
      ${this._panelOpen ? this._renderPanel() : nothing}
    `;
  }
}

declare global {
  interface HTMLElementTagNameMap {
    'cp-founder-ticket-card': CpFounderTicketCard;
  }
}
